import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  Stack,
  TextField,
  Button,
  MenuItem,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { toast } from 'react-toastify';
import Services from 'src/services/Services';

const mockEditOptions = [
  'Sağlam',
  'Kulp Kırık',
  'Müşteri Şifresi Çalışmıyor',
  'Pil Bitik',
  'Master Key Çalışmıyor',
  'Tuş Takımı Arızalı',
  'Kilit Dili Arızalı',
  'Kilit Dili Karşılığı Arızalı',
  'Raf Arızası',
  'Kilit Yok',
  'Dolap Numarası Yok',
];

const redButtonStyle = {
  backgroundColor: '#D92D20',
  color: '#FFFFFF',
  '&:hover': { backgroundColor: '#B42318' },
  borderRadius: '8px',
  textTransform: 'none',
  px: 2,
  py: 1,
  '&.Mui-disabled': { backgroundColor: '#F2F4F7', color: '#98A2B3' },
};
const whiteButtonStyle = {
  color: '#344054',
  borderColor: '#D0D5DD',
  '&:hover': { borderColor: '#B4BCCB', backgroundColor: 'transparent' },
  borderRadius: '8px',
  textTransform: 'none',
};

const formatDate = (dateString) => {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    return date.toLocaleString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (error) {
    return '-';
  }
};

export default function BoxStatusPage() {
  const [page, setPage] = useState(1);

  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [showLockerTable, setShowLockerTable] = useState(false);
  const [rows, setRows] = useState([]);
  const [unfilteredLockers, setUnfilteredLockers] = useState([]);
  const [editingRowId, setEditingRowId] = useState(null);
  const [loadingLocations, setLoadingLocations] = useState(true);
  const [loadingLockers, setLoadingLockers] = useState(false);
  const [filterLockerNo, setFilterLockerNo] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [updatingLockerId, setUpdatingLockerId] = useState(null);
  const [locationSearchText, setLocationSearchText] = useState('');
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState({
    title: '',
    message: '',
    onConfirm: () => {},
  });
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [infoModalData, setInfoModalData] = useState(null);

  useEffect(() => {
    const fetchLocations = async () => {
      setLoadingLocations(true);
      try {
        const response = await Services.GetDropboxLockerLocations();
        if (response.data.status && Array.isArray(response.data.result)) {
          const mappedLocations = response.data.result.map((loc) => ({
            name: loc.name,
            code: loc.locationCode,
          }));

          mappedLocations.sort((a, b) => a.name.localeCompare(b.name, 'tr'));

          setLocations(mappedLocations);
        } else {
          toast.error('Lokasyonlar getirilemedi.');
        }
      } catch (error) {
        toast.error('Lokasyonlar getirilirken bir hata oluştu.');
      } finally {
        setLoadingLocations(false);
      }
    };
    fetchLocations();
  }, []);

  const handleFetchLockers = async () => {
    if (!selectedLocation) return;
    setLoadingLockers(true);
    setShowLockerTable(true);
    try {
      const response = await Services.getDropboxStatuses(selectedLocation);
      if (response.data.status && Array.isArray(response.data.result)) {
        const lockerData = response.data.result.map((locker) => ({
          id: locker.lockerNumber,
          availability: locker.isAvailable ? 'Boş' : 'Dolu',
          status: 'Sağlam',
          qrCode: locker.qrCode,
          password:
            !locker.isAvailable && locker.lockPass ? locker.lockPass : '-',
          startDate: formatDate(locker.startDate),
          endDate: formatDate(locker.endDate),
          rawData: locker,
        }));
        setRows(lockerData);
        setUnfilteredLockers(lockerData);
      } else {
        toast.error('Bu lokasyon için dolap verisi bulunamadı.');
      }
    } catch (error) {
      toast.error('Dolaplar getirilirken bir hata oluştu.');
    } finally {
      setLoadingLockers(false);
    }
  };

  const handleLoadLocker = async (qrCode, lockerId) => {
    setUpdatingLockerId(lockerId);
    try {
      const payload = {
        qrCode: qrCode,
        lockerNumber: lockerId,
        locationCode: selectedLocation,
      };

      const response = await Services.MakeRentalLoad(payload);

      if (response.data) {
        setInfoModalData(response.data.result.loadData);
        setIsInfoModalOpen(true);
        await handleFetchLockers();
      } else {
        toast.error(response.data.message || 'İşlem başarısız oldu.');
      }
    } catch (error) {
      toast.error('İşlem sırasında bir hata oluştu.');
    } finally {
      setUpdatingLockerId(null);
    }
  };

  const handleFinishLocker = async (qrCode, lockerId) => {
    setUpdatingLockerId(lockerId);
    try {
      const response = await Services.FinishRentalLoad(qrCode);
      if (response.data) {
        toast.success(`'${lockerId}' numaralı dolap başarıyla boşaltıldı.`);
        await handleFetchLockers();
      } else {
        toast.error('Beklenmedik bir yanıt alındı.');
      }
    } catch (error) {
      console.error('Boşaltma işlemi hatası:', error);
      toast.error('İşlem sırasında bir sunucu hatası oluştu.');
    } finally {
      setUpdatingLockerId(null);
    }
  };

  const openConfirmationModal = (title, message, onConfirm) => {
    setModalContent({ title, message, onConfirm });
    setIsConfirmModalOpen(true);
  };

  const handleConfirm = () => {
    modalContent.onConfirm();
    setIsConfirmModalOpen(false);
  };

  const handleFilter = () => {
    let filteredData = [...unfilteredLockers];
    if (filterLockerNo)
      filteredData = filteredData.filter((row) =>
        String(row.id).includes(filterLockerNo)
      );
    if (filterStatus)
      filteredData = filteredData.filter((row) => row.status === filterStatus);
    setRows(filteredData);
    setPage(1);
  };

  const handleEditClick = (rowId) => setEditingRowId(rowId);

  const handleStatusUpdate = (newStatus, rowId) => {
    setRows((prevRows) =>
      prevRows.map((row) =>
        row.id === rowId ? { ...row, status: newStatus } : row
      )
    );
    setEditingRowId(null);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(Number(event.target.value));
    setPage(1);
  };

  return (
    <Container maxWidth={false} sx={{ mt: 2, mb: 4, maxWidth: '1720px' }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h5" fontWeight="bold">
          {selectedLocation
            ? `Dolap Durumu (${
                locations.find((loc) => loc.code === selectedLocation)?.name ||
                ''
              })`
            : 'Dolap Durumu'}
        </Typography>
      </Stack>

      <Card
        sx={{ boxShadow: 'none', border: '1px solid #EAECF0', p: 3, mb: 3 }}
      >
        {loadingLocations ? (
          <CircularProgress />
        ) : (
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField
              select
              label="Nokta Seçimi"
              value={selectedLocation}
              onChange={(e) => setSelectedLocation(e.target.value)}
              size="small"
              sx={{
                minWidth: 300,
                '& .MuiOutlinedInput-root': { borderRadius: '8px' },
              }}
            >
              <Box onKeyDown={(e) => e.stopPropagation()} sx={{ p: 1, pt: 0 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Nokta adı arayın..."
                  value={locationSearchText}
                  onChange={(e) => setLocationSearchText(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
              {locations
                .filter((loc) =>
                  loc.name
                    .toLowerCase()
                    .includes(locationSearchText.toLowerCase())
                )
                .map((loc) => (
                  <MenuItem key={loc.code} value={loc.code}>
                    {loc.name}
                  </MenuItem>
                ))}
            </TextField>
            <Button
              variant="contained"
              sx={redButtonStyle}
              onClick={handleFetchLockers}
              disabled={!selectedLocation || loadingLockers}
            >
              {loadingLockers ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Dolapları Getir'
              )}
            </Button>
          </Stack>
        )}
      </Card>

      {showLockerTable && (
        <Card sx={{ boxShadow: 'none', border: '1px solid #EAECF0' }}>
          <Box sx={{ p: 2 }}>
            <Stack direction="row" spacing={2} justifyContent="space-between">
              <Stack direction="row" spacing={1.5} flexGrow={1}>
                <TextField
                  size="small"
                  placeholder="Dolap No arayın..."
                  value={filterLockerNo}
                  onChange={(e) => setFilterLockerNo(e.target.value)}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
                {/*
                
                <TextField
                  select
                  label="Dolap Durumu"
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  size="small"
                  sx={{
                    minWidth: 200,
                    '& .MuiOutlinedInput-root': { borderRadius: '8px' },
                  }}
                >
                  <MenuItem value="">Tümü</MenuItem>
                  {mockEditOptions.map((opt) => (
                    <MenuItem key={opt} value={opt}>
                      {opt}
                    </MenuItem>
                  ))}
                </TextField>

                */}
                <Button
                  variant="contained"
                  sx={redButtonStyle}
                  onClick={handleFilter}
                >
                  Filtrele
                </Button>
              </Stack>
            </Stack>
          </Box>
          {loadingLockers ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead sx={{ backgroundColor: '#F9FAFB' }}>
                    <TableRow>
                      <TableCell>DOLAP NO</TableCell>
                      <TableCell>İŞLEM</TableCell>
                      <TableCell>ŞİFRE</TableCell>
                      <TableCell>DOLAP DURUMU</TableCell>
                      {/*
                                            <TableCell>QR KODU</TableCell>
                                            */}
                      <TableCell>BAŞLANGIÇ TARİHİ</TableCell>
                      <TableCell>BİTİŞ TARİHİ</TableCell>
                      {/*
                                            <TableCell>DURUM DÜZENLE</TableCell>
                                            */}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows
                      .slice((page - 1) * rowsPerPage, page * rowsPerPage)
                      .map((row) => (
                        <TableRow
                          key={row.id}
                          sx={{ '& td, & th': { borderColor: '#EAECF0' } }}
                        >
                          <TableCell>
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={1}
                            >
                              <FiberManualRecordIcon
                                sx={{
                                  fontSize: 10,
                                  color:
                                    row.availability === 'Dolu'
                                      ? '#F04438'
                                      : '#12B76A',
                                }}
                              />
                              <Typography variant="body2" fontWeight={500}>
                                {row.id}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell>
                            {updatingLockerId === row.id ? (
                              <CircularProgress size={24} />
                            ) : row.availability === 'Dolu' ? (
                              <Button
                                variant="outlined"
                                size="small"
                                sx={{ ...whiteButtonStyle, py: 0.5 }}
                                onClick={() =>
                                  openConfirmationModal(
                                    'Dolabı Boşalt',
                                    `'${row.id}' numaralı dolabı boşaltmak istediğinizden emin misiniz?`,
                                    () =>
                                      handleFinishLocker(
                                        row.rawData.qrCode,
                                        row.id
                                      )
                                  )
                                }
                                disabled={updatingLockerId !== null}
                              >
                                Boşalt
                              </Button>
                            ) : (
                              <Button
                                variant="contained"
                                size="small"
                                sx={{ ...redButtonStyle, py: 0.5 }}
                                onClick={() =>
                                  openConfirmationModal(
                                    'Şifre Al',
                                    `'${row.id}' numaralı dolap için şifre almak istediğinizden emin misiniz?`,
                                    () =>
                                      handleLoadLocker(
                                        row.rawData.qrCode,
                                        row.id
                                      )
                                  )
                                }
                                disabled={updatingLockerId !== null}
                              >
                                Şifre Al
                              </Button>
                            )}
                          </TableCell>
                          <TableCell>{row.password}</TableCell>
                          <TableCell>{row.status}</TableCell>
                          {/*
                                                <TableCell>{row.qrCode}</TableCell>
                                                */}
                          <TableCell>{row.startDate}</TableCell>
                          <TableCell>{row.endDate}</TableCell>
                          {/*                                                 
                                                    <TableCell sx={{ minWidth: '200px' }}>{editingRowId === row.id ? (<TextField select autoFocus fullWidth size="small" value={row.status} onChange={(e) => handleStatusUpdate(e.target.value, row.id)} onBlur={() => setEditingRowId(null)}>{mockEditOptions.map((option) => (<MenuItem key={option} value={option}>{option}</MenuItem>))}</TextField>) : (<Button fullWidth variant="outlined" sx={whiteButtonStyle} startIcon={<EditOutlinedIcon />} onClick={() => handleEditClick(row.id)}>Düzenle</Button>)}</TableCell>
                                                */}
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ p: 2 }}
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="body2">Sayfa başına satır:</Typography>
                  <TextField
                    select
                    size="small"
                    value={rowsPerPage}
                    onChange={handleChangeRowsPerPage}
                    sx={{
                      '& .MuiInputBase-root': { borderRadius: '6px' },
                      width: '70px',
                    }}
                  >
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={25}>25</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                  </TextField>
                </Stack>

                <Stack direction="row" alignItems="center" spacing={2}>
                  <Typography variant="body2" color="text.secondary">
                    Toplam {rows.length} kayıttan{' '}
                    {rows.length > 0 ? (page - 1) * rowsPerPage + 1 : 0}–
                    {Math.min(page * rowsPerPage, rows.length)} arası
                    gösteriliyor
                  </Typography>
                  <Pagination
                    count={Math.ceil(rows.length / rowsPerPage)}
                    page={page}
                    onChange={(e, v) => setPage(v)}
                    color="primary"
                    shape="rounded"
                  />
                </Stack>
              </Stack>
            </>
          )}
        </Card>
      )}

      <Dialog
        open={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
        PaperProps={{ sx: { borderRadius: '12px', width: 400 } }}
      >
        <DialogTitle fontWeight={600}>{modalContent.title}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {modalContent.message}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button
            variant="outlined"
            sx={whiteButtonStyle}
            onClick={() => setIsConfirmModalOpen(false)}
          >
            Vazgeç
          </Button>
          <Button
            variant="contained"
            sx={redButtonStyle}
            onClick={handleConfirm}
            autoFocus
          >
            Onayla
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isInfoModalOpen}
        onClose={() => setIsInfoModalOpen(false)}
        PaperProps={{ sx: { borderRadius: '12px', width: 400, p: 2 } }}
      >
        <DialogContent>
          <Stack alignItems="center" spacing={2} textAlign="center">
            <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main' }} />
            <Typography variant="h6" fontWeight={600}>
              İşlem Başarılı
            </Typography>
            <Typography variant="body1">
              {infoModalData?.lockerNumber} numaralı dolap için şifreniz
              oluşturulmuştur.
            </Typography>
            <Box
              sx={{
                p: 2,
                backgroundColor: '#F2F4F7',
                borderRadius: '8px',
                width: '100%',
              }}
            >
              <Typography variant="body2" color="text.secondary">
                DOLAP ŞİFRESİ
              </Typography>
              <Typography variant="h5" fontWeight={700} letterSpacing={2}>
                {infoModalData?.otpCode}
              </Typography>
            </Box>
            <Typography variant="body2">
              Şifre son kullanım tarihi:{' '}
              <strong>{formatDate(infoModalData?.endDate)}</strong>
            </Typography>
            <Button
              onClick={() => setIsInfoModalOpen(false)}
              variant="contained"
              sx={redButtonStyle}
              fullWidth
            >
              Kapat
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>
    </Container>
  );
}
